type Translate = (key: string, options?: Record<string, unknown>) => string;

export type RequestConfirmationStatus = 'confirmed' | 'unconfirmed';

export async function waitForRequestConfirmation(
  request: Promise<unknown>,
  timeoutMs = 10000,
): Promise<RequestConfirmationStatus> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request.then(() => 'confirmed' as const),
      new Promise<'unconfirmed'>((resolve) => {
        timeoutId = setTimeout(() => resolve('unconfirmed'), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
  }
}

export function getAuthErrorCode(error: unknown): string {
  if (error && typeof error === 'object') {
    const candidate = error as { code?: unknown; name?: unknown };
    if (typeof candidate.code === 'string') return candidate.code;
    if (typeof candidate.name === 'string' && candidate.name !== 'Error') {
      return candidate.name;
    }
  }
  return 'UnknownError';
}

export function getAuthErrorMessage(t: Translate, error: unknown): string {
  const code = getAuthErrorCode(error);
  const rawMessage =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : String(error);
  const message = rawMessage.toLowerCase();
  if (
    message.includes('invalid code provided') ||
    message.includes('code already used') ||
    message.includes('not businessuser')
  ) {
    return t('voucher_code_invalid');
  }
  if (
    code === 'NetworkError' ||
    code === 'TypeError' ||
    message.includes('network') ||
    message.includes('internet')
  ) {
    return t('AUTH_NETWORK_ERROR');
  }
  if (
    code === 'TooManyRequestsException' ||
    code === 'LimitExceededException'
  ) {
    return t('AUTH_RATE_LIMIT_ERROR');
  }
  if (
    code === 'UserUnAuthenticatedException' ||
    (code === 'NotAuthorizedException' && message.includes('session'))
  ) {
    return t('AUTH_SESSION_EXPIRED');
  }
  if (code === 'CodeMismatchException') return t('ACTIVATION_CODE_INVALID');
  if (code === 'ExpiredCodeException') return t('CODE_EXPIRED');
  if (code === 'UserNotConfirmedException') return t('CONFIRM_ACCOUNT_FIRST');
  return t('AUTH_UNEXPECTED_ERROR', { code });
}

export function getResponseStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
}

export function isVoucherRejected(error: unknown): boolean {
  const status = getResponseStatus(error);
  return status === 400 || status === 404 || status === 409;
}

export function getVoucherErrorMessage(t: Translate, error: unknown): string {
  if (isVoucherRejected(error)) return t('voucher_code_invalid_desc');
  const status = getResponseStatus(error);
  if (status === 401 || status === 403) return t('AUTH_SESSION_EXPIRED');
  return status == null
    ? t('VOUCHER_NETWORK_ERROR')
    : t('VOUCHER_SERVICE_ERROR');
}
